import { filter, pipe } from 'ramda';
import PCNode from '../../MineModel/Nodes/PCNode';
import PCSpool from '../../MineModel/Spools/PCSpool';

export interface Point3 {
  x: number;
  y: number;
  z: number;
}

export interface SolidBoundingBox {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  minZ: number;
  maxZ: number;
  centerX?: number;
  centerY?: number;
  centerZ?: number;
}

export interface SearchBox {
  plusX: number;
  minusX: number;
  plusY: number;
  minusY: number;
  plusZ: number;
  minusZ: number;
}

export type AccessType = 'top' | 'bottom';

export type AssignmentStatus =
  | 'mark_existing_node'
  | 'insert_between_nodes'
  | 'update_existing'
  | 'manual_review'
  | 'skipped_existing';

export interface AssignmentResult {
  solidGuid: string;
  status: AssignmentStatus;
  existingNodeId?: string;
  existingStopeId?: string;
  nodeAId?: string;
  nodeBId?: string;
  newNodePosition?: Point3;
  reason?: string;
}

export interface AutoAssignSolidInput {
  guid: string;
  name: string;
  boundingBox: SolidBoundingBox;
}

export interface DuplicateStopeName {
  name: string;
  count: number;
}

export const TOP_ACCESS_DEFAULT_SEARCH_BOX: SearchBox = {
  plusX: 1,
  minusX: 1,
  plusY: 1,
  minusY: 1,
  plusZ: 5,
  minusZ: 5,
};

export const BOTTOM_ACCESS_DEFAULT_SEARCH_BOX: SearchBox = {
  plusX: 1,
  minusX: 1,
  plusY: 1,
  minusY: 1,
  plusZ: 0,
  minusZ: 5,
};

export const defaultSearchBoxForAccess = (accessType: AccessType): SearchBox => (
  accessType === 'top' ? TOP_ACCESS_DEFAULT_SEARCH_BOX : BOTTOM_ACCESS_DEFAULT_SEARCH_BOX
);

/**
 * Top access searches above the stope top first, and only when that finds nothing searches `minusZ` down into the
 * solid, where an access drive cut just below the crown sits.
 */
export const searchPasses = (accessType: AccessType, searchBox: SearchBox): SearchBox[] => (
  accessType === 'top' && searchBox.minusZ > 0
    ? [{ ...searchBox, minusZ: 0 }, { ...searchBox, plusZ: 0 }]
    : [searchBox]
);

const resolveCenter = (bbox: SolidBoundingBox): Point3 => ({
  x: bbox.centerX ?? (bbox.minX + bbox.maxX) / 2,
  y: bbox.centerY ?? (bbox.minY + bbox.maxY) / 2,
  z: bbox.centerZ ?? (bbox.minZ + bbox.maxZ) / 2,
});

export const computeProbePoint = (bbox: SolidBoundingBox, accessType: AccessType): Point3 => {
  const center = resolveCenter(bbox);
  return {
    x: center.x,
    y: center.y,
    z: accessType === 'top' ? bbox.maxZ : bbox.minZ,
  };
};

export const computeStopeCentroid = (bbox: SolidBoundingBox, probeZ: number): Point3 => {
  const center = resolveCenter(bbox);
  return { x: center.x, y: center.y, z: probeZ };
};

export const insideAsymmetricAABB = (point: Point3, probe: Point3, searchBox: SearchBox): boolean => (
  point.x >= probe.x - searchBox.minusX
  && point.x <= probe.x + searchBox.plusX
  && point.y >= probe.y - searchBox.minusY
  && point.y <= probe.y + searchBox.plusY
  && point.z >= probe.z - searchBox.minusZ
  && point.z <= probe.z + searchBox.plusZ
);

export const insideHorizontalBBox = (point: Point3, bbox: SolidBoundingBox): boolean => (
  point.x >= bbox.minX
  && point.x <= bbox.maxX
  && point.y >= bbox.minY
  && point.y <= bbox.maxY
);

interface ClosestPointResult {
  point: Point3;
  t: number;
  distanceSq: number;
}

export const closestPointOnSegment = (a: Point3, b: Point3, p: Point3): ClosestPointResult => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dz = b.z - a.z;
  const lengthSq = dx * dx + dy * dy + dz * dz;

  if (lengthSq === 0) {
    const diffX = p.x - a.x;
    const diffY = p.y - a.y;
    const diffZ = p.z - a.z;
    return {
      point: { x: a.x, y: a.y, z: a.z },
      t: 0,
      distanceSq: diffX * diffX + diffY * diffY + diffZ * diffZ,
    };
  }

  const rawT = ((p.x - a.x) * dx + (p.y - a.y) * dy + (p.z - a.z) * dz) / lengthSq;
  const t = Math.max(0, Math.min(1, rawT));
  const point: Point3 = {
    x: a.x + t * dx,
    y: a.y + t * dy,
    z: a.z + t * dz,
  };
  const diffX = p.x - point.x;
  const diffY = p.y - point.y;
  const diffZ = p.z - point.z;
  return {
    point,
    t,
    distanceSq: diffX * diffX + diffY * diffY + diffZ * diffZ,
  };
};

const distanceSq3 = (a: Point3, b: Point3): number => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return dx * dx + dy * dy + dz * dz;
};

const nodeToPoint = (node: PCNode): Point3 => ({
  x: node.coords.x,
  y: node.coords.y,
  z: node.coords.z,
});

export const findCandidateNodes = (
  nodes: PCNode[],
  probe: Point3,
  searchBox: SearchBox,
  reservedIds: Set<string>,
): PCNode[] => filter((node: PCNode) => (
  !node.isStope
  && !reservedIds.has(node.id)
  && insideAsymmetricAABB(nodeToPoint(node), probe, searchBox)
), nodes);

const spoolBoundingBoxOverlapsSearchBox = (spool: PCSpool, probe: Point3, searchBox: SearchBox): boolean => {
  const ax = spool.pointA.coords.x;
  const ay = spool.pointA.coords.y;
  const az = spool.pointA.coords.z;
  const bx = spool.pointB.coords.x;
  const by = spool.pointB.coords.y;
  const bz = spool.pointB.coords.z;

  const minX = Math.min(ax, bx);
  const maxX = Math.max(ax, bx);
  const minY = Math.min(ay, by);
  const maxY = Math.max(ay, by);
  const minZ = Math.min(az, bz);
  const maxZ = Math.max(az, bz);

  const boxMinX = probe.x - searchBox.minusX;
  const boxMaxX = probe.x + searchBox.plusX;
  const boxMinY = probe.y - searchBox.minusY;
  const boxMaxY = probe.y + searchBox.plusY;
  const boxMinZ = probe.z - searchBox.minusZ;
  const boxMaxZ = probe.z + searchBox.plusZ;

  return maxX >= boxMinX && minX <= boxMaxX
    && maxY >= boxMinY && minY <= boxMaxY
    && maxZ >= boxMinZ && minZ <= boxMaxZ;
};

export const findCandidateSpools = (spools: PCSpool[], probe: Point3, searchBox: SearchBox): PCSpool[] => (
  filter((spool: PCSpool) => spoolBoundingBoxOverlapsSearchBox(spool, probe, searchBox), spools)
);

const pickNearestNode = (candidates: PCNode[], centroid: Point3): PCNode | undefined => {
  if (candidates.length === 0) return undefined;
  return candidates.reduce<{ node: PCNode; distanceSq: number } | undefined>((best, node) => {
    const d = distanceSq3(nodeToPoint(node), centroid);
    if (!best || d < best.distanceSq) return { node, distanceSq: d };
    return best;
  }, undefined)?.node;
};

interface SpoolPick {
  spool: PCSpool;
  closestPoint: Point3;
  distanceSq: number;
}

const pickNearestSpool = (
  spools: PCSpool[],
  probe: Point3,
  centroid: Point3,
  searchBox: SearchBox,
): SpoolPick | undefined => spools.reduce<SpoolPick | undefined>((best, spool) => {
  const a: Point3 = { x: spool.pointA.coords.x, y: spool.pointA.coords.y, z: spool.pointA.coords.z };
  const b: Point3 = { x: spool.pointB.coords.x, y: spool.pointB.coords.y, z: spool.pointB.coords.z };
  const { point, distanceSq } = closestPointOnSegment(a, b, centroid);
  if (!insideAsymmetricAABB(point, probe, searchBox)) return best;
  if (!best || distanceSq < best.distanceSq) return { spool, closestPoint: point, distanceSq };
  return best;
}, undefined);

export interface AssignSolidContext {
  nodes: PCNode[];
  spools: PCSpool[];
  accessType: AccessType;
  searchBox: SearchBox;
  reservedNodeIds: Set<string>;
}

const searchOneBox = (
  solid: AutoAssignSolidInput,
  probe: Point3,
  centroid: Point3,
  searchBox: SearchBox,
  { nodes, spools, reservedNodeIds }: AssignSolidContext,
): AssignmentResult | undefined => {
  const nearestNode = pickNearestNode(findCandidateNodes(nodes, probe, searchBox, reservedNodeIds), centroid);
  if (nearestNode) {
    return {
      solidGuid: solid.guid,
      status: 'mark_existing_node',
      existingNodeId: nearestNode.id,
    };
  }

  const spoolPick = pickNearestSpool(findCandidateSpools(spools, probe, searchBox), probe, centroid, searchBox);
  if (!spoolPick) return undefined;

  const horizontallyInside = insideHorizontalBBox(spoolPick.closestPoint, solid.boundingBox);
  const newNodePosition: Point3 = horizontallyInside
    ? spoolPick.closestPoint
    : { x: centroid.x, y: centroid.y, z: spoolPick.closestPoint.z };
  return {
    solidGuid: solid.guid,
    status: 'insert_between_nodes',
    nodeAId: spoolPick.spool.pointA.id,
    nodeBId: spoolPick.spool.pointB.id,
    newNodePosition,
  };
};

export const assignSolid = (
  solid: AutoAssignSolidInput,
  context: AssignSolidContext,
): AssignmentResult => {
  const { accessType, searchBox } = context;
  const probe = computeProbePoint(solid.boundingBox, accessType);
  const centroid = computeStopeCentroid(solid.boundingBox, probe.z);

  const found = searchPasses(accessType, searchBox).reduce<AssignmentResult | undefined>(
    (result, box) => result ?? searchOneBox(solid, probe, centroid, box, context),
    undefined,
  );

  return found ?? {
    solidGuid: solid.guid,
    status: 'manual_review',
    reason: 'No candidate node or spool inside search box',
  };
};

export interface RunAutoAssignOptions {
  solids: AutoAssignSolidInput[];
  nodes: PCNode[];
  spools: PCSpool[];
  accessType: AccessType;
  searchBox: SearchBox;
  existingStopesByName: Record<string, string>;
  existingStopeBehavior: 'update' | 'skip';
}

export const normalizeStopeName = (name: string): string => name.trim().toLowerCase();

export const findDuplicateStopeNames = (names: string[]): DuplicateStopeName[] => {
  const counts = new Map<string, DuplicateStopeName>();

  names.forEach((name) => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    const key = normalizeStopeName(trimmedName);
    const existing = counts.get(key);
    if (existing) {
      counts.set(key, { ...existing, count: existing.count + 1 });
      return;
    }

    counts.set(key, { name: trimmedName, count: 1 });
  });

  return Array.from(counts.values())
    .filter((entry) => entry.count > 1)
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
};

export interface NamedSolid {
  guid: string;
  name: string;
}

const nextFreeSuffix = (base: string, suffix: number, taken: Set<string>): number => (
  taken.has(normalizeStopeName(`${base}-${suffix}`)) ? nextFreeSuffix(base, suffix + 1, taken) : suffix
);

/**
 * Every solid whose name is shared (case-insensitively) becomes name-1, name-2, … in file order, so re-importing the
 * same file yields the same names. Suffixes another solid already uses are skipped. Returns only the renamed solids.
 */
export const renameDuplicateSolids = (solids: NamedSolid[]): Record<string, string> => {
  const groups = solids.reduce<Map<string, NamedSolid[]>>((acc, solid) => {
    const key = normalizeStopeName(solid.name);
    if (key) acc.set(key, [...(acc.get(key) ?? []), solid]);
    return acc;
  }, new Map());
  const taken = new Set(solids.map((solid) => normalizeStopeName(solid.name)));

  return Array.from(groups.values())
    .filter((members) => members.length > 1)
    .reduce<Record<string, string>>((renamed, members) => {
      members.reduce((suffix, member) => {
        const base = member.name.trim();
        const free = nextFreeSuffix(base, suffix, taken);
        const name = `${base}-${free}`;
        taken.add(normalizeStopeName(name));
        Object.assign(renamed, { [member.guid]: name });
        return free + 1;
      }, 1);
      return renamed;
    }, {});
};

export const runAutoAssign = (options: RunAutoAssignOptions): AssignmentResult[] => {
  const {
    solids,
    nodes,
    spools,
    accessType,
    searchBox,
    existingStopesByName,
    existingStopeBehavior,
  } = options;

  const reservedNodeIds = new Set<string>();

  const nonStopeNodes = pipe(
    filter((n: PCNode) => !n.isStope),
  )(nodes);

  return solids.map((solid) => {
    const normalizedName = normalizeStopeName(solid.name);
    const existingStopeId = existingStopesByName[normalizedName];

    if (existingStopeId) {
      if (existingStopeBehavior === 'skip') {
        return {
          solidGuid: solid.guid,
          status: 'skipped_existing',
          reason: 'Stope name already exists',
        };
      }
      return {
        solidGuid: solid.guid,
        status: 'update_existing',
        existingStopeId,
      };
    }

    const result = assignSolid(solid, {
      nodes: nonStopeNodes,
      spools,
      accessType,
      searchBox,
      reservedNodeIds,
    });

    if (result.status === 'mark_existing_node' && result.existingNodeId) {
      reservedNodeIds.add(result.existingNodeId);
    }
    return result;
  });
};
